/**
 * A card-only frame's detail, fetched once per frame however many callers
 * wait for it, and the one writer of whole-frame metadata, which keeps a
 * card's summary subset from being saved over the stored metadata.
 */
import { handleApiError } from "@/api/error-middleware";
import { getFrame } from "@/api/frame-listing";
import type { Frame } from "@/api/spaces";
import {
  frameDataPatchFromApi,
  isNewerServerTime,
  type FrameData,
} from "@/lib/frame-data";
import { withStoreKeptOnMirror } from "@/lib/mirrored-store";
import { framePlacementOf } from "@/stores/canvas-frame-geometry";
import { placeFrameNodes } from "@/stores/canvas-frame-nodes";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";

interface FrameDetailRequest {
  promise: Promise<FrameData>;
  controller: AbortController;
  /** Callers still waiting; the request aborts when the last one leaves. */
  waiting: number;
  settled: boolean;
}

const detailRequests = new Map<string, FrameDetailRequest>();

function detailAborted(): DOMException {
  return new DOMException("Frame detail request aborted", "AbortError");
}

/**
 * The detail upgrades a card frame to `"full"`; a frame made full meanwhile
 * is kept. Its other fields apply only when the response is not older than
 * what a websocket event already brought. A summary-loaded frame gets its
 * canvas node here, from the detail's geometry (Part 05's natural size reads
 * it until the canvas goes). On a mirror the node stays in this window (the
 * owner's natural size falls back to the created size) while the upgraded
 * frame reaches the other windows as a one-frame delta: a focus opened here
 * shows there too.
 */
function ingestDetail(frame: Frame): FrameData {
  const { framesById, patchFrame } = useFramesStore.getState();
  const current = framesById[frame.id];
  if (!current) throw new Error(`Frame ${frame.id} is no longer loaded`);
  if (current.contentState === "card") {
    const patch = frameDataPatchFromApi(frame);
    const older =
      current.updatedAt !== undefined &&
      isNewerServerTime(current.updatedAt, frame.updated_at);
    const hasNode = useCanvasStore
      .getState()
      .nodes.some((node) => node.id === frame.id);
    withStoreKeptOnMirror("canvas", () =>
      placeFrameNodes(
        new Map(hasNode ? [] : [[frame.id, framePlacementOf(frame)]]),
        { load: false },
        () =>
          patchFrame(
            frame.id,
            older ? { content: patch.content, card: patch.card } : patch,
          ),
      ),
    );
  }
  return useFramesStore.getState().framesById[frame.id]!;
}

function startDetailRequest(
  frameId: string,
  projectId: string,
): FrameDetailRequest {
  const controller = new AbortController();
  const promise = getFrame(projectId, frameId, {
    signal: controller.signal,
  }).then(ingestDetail, (error: unknown) => {
    if (!controller.signal.aborted) handleApiError(error);
    throw error;
  });
  const request: FrameDetailRequest = {
    promise,
    controller,
    waiting: 0,
    settled: false,
  };
  const forget = () => {
    request.settled = true;
    if (detailRequests.get(frameId) === request) {
      detailRequests.delete(frameId);
    }
  };
  // Every waiter handles the outcome; this keeps an abandoned one quiet.
  promise.then(forget, forget);
  detailRequests.set(frameId, request);
  return request;
}

function awaitDetail(
  frameId: string,
  request: FrameDetailRequest,
  signal: AbortSignal | undefined,
): Promise<FrameData> {
  if (signal?.aborted) return Promise.reject(detailAborted());
  request.waiting += 1;
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      request.waiting -= 1;
      if (request.waiting === 0 && !request.settled) {
        request.controller.abort();
        if (detailRequests.get(frameId) === request) {
          detailRequests.delete(frameId);
        }
      }
      reject(detailAborted());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    request.promise.then(
      (frame) => {
        signal?.removeEventListener("abort", onAbort);
        resolve(frame);
      },
      (error: unknown) => {
        signal?.removeEventListener("abort", onAbort);
        reject(error);
      },
    );
  });
}

/** True when the frame is loaded from a summary only: `content` is empty until its detail loads. */
export function isCardOnlyFrame(frameId: string): boolean {
  return useFramesStore.getState().framesById[frameId]?.contentState === "card";
}

/**
 * The frame with its full content: a card-only frame fetches its detail
 * once (concurrent callers share one request) and becomes `"full"`. A
 * caller's `signal` stops its wait; the request aborts when no caller is
 * left. A failed fetch shows the API error toast once and rejects.
 */
export function ensureFrameDetail(
  frameId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<FrameData> {
  const frame = useFramesStore.getState().framesById[frameId];
  if (!frame) {
    return Promise.reject(new Error(`Frame ${frameId} is not loaded`));
  }
  if (frame.contentState !== "card") return Promise.resolve(frame);
  let request = detailRequests.get(frameId);
  if (!request) {
    const projectId =
      frame.spaceId ?? useProjectStore.getState().currentProject?.id;
    if (!projectId) {
      return Promise.reject(new Error("Project context is unavailable."));
    }
    request = startDetailRequest(frameId, projectId);
  }
  return awaitDetail(frameId, request, signal);
}

type FrameMetadata = Record<string, unknown>;

/** The metadata shown before a card-only frame's pending changes; reverted to if its detail fails. */
const metadataBeforeDetail = new Map<string, FrameMetadata>();

/**
 * The one writer of a frame's whole `metadata` (pin, visibility, font
 * size): `change` derives the new metadata (null for no change) and `save`
 * persists it whole. The change shows at once. A card-only frame holds only
 * the summary's subset and saving that would erase the keys it leaves out,
 * so its save waits for the detail and applies `change` to the full metadata
 * then (two quick toggles stay two). A failed detail reverts what was shown;
 * its error toast has shown.
 */
export function changeFrameMetadata(
  frameId: string,
  change: (metadata: FrameMetadata) => FrameMetadata | null,
  save: (metadata: FrameMetadata) => void,
): void {
  const frame = useFramesStore.getState().framesById[frameId];
  if (!frame) return;
  const shown = change(frame.metadata);
  if (!shown) return;
  useFramesStore.getState().patchFrame(frameId, { metadata: shown });
  if (frame.contentState !== "card") {
    save(shown);
    return;
  }
  if (!metadataBeforeDetail.has(frameId)) {
    metadataBeforeDetail.set(frameId, frame.metadata);
  }
  void ensureFrameDetail(frameId).then(
    () => {
      metadataBeforeDetail.delete(frameId);
      const full = useFramesStore.getState().framesById[frameId];
      const metadata = full && change(full.metadata);
      if (!metadata) return;
      useFramesStore.getState().patchFrame(frameId, { metadata });
      save(metadata);
    },
    () => {
      const before = metadataBeforeDetail.get(frameId);
      metadataBeforeDetail.delete(frameId);
      if (before) {
        useFramesStore.getState().patchFrame(frameId, { metadata: before });
      }
    },
  );
}
